export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, init);
  const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    if (res.status === 401 && !url.startsWith('/api/auth/')) window.dispatchEvent(new Event('tj:unauthorized'));
    throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  del: <T>(url: string, body?: unknown) => request<T>('DELETE', url, body),
};

export function qs(params: Record<string, string | number | undefined | null>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ---- Shared types ----

export type Trade = {
  id: number; trade_key: string; account: string; symbol: string; side: 'long' | 'short'; status: 'open' | 'closed';
  open_time: number; close_time: number | null; trade_date: string; shares: number; max_position: number;
  entry_price: number; exit_price: number | null; gross_pnl: number; fees: number; net_pnl: number; exec_count: number;
  setup: string | null; mistakes: string[]; rating: number | null; risk: number | null; tags: string[]; screenshots: number; hasNotes?: boolean;
  commission: number; ecn_fees: number; other_fees: number; volume: number; entry_added_pct: number | null; exit_added_pct: number | null;
  position_mfe?: number | null; position_mae?: number | null; exit_efficiency?: number | null;
};

export type TradeMetrics = {
  bar_interval: string | null; position_mfe: number | null; position_mae: number | null; price_mfe: number | null; price_mae: number | null;
  time_to_mfe_ms: number | null; time_to_mae_ms: number | null; best_exit_pnl: number | null; exit_efficiency: number | null;
  gap_pct: number | null; atr14: number | null; entry_atr: number | null; range_atr: number | null; day_volume: number | null; rel_volume: number | null;
  error: string | null;
};

export type Execution = {
  id: number; side: string; price: number; time: number; fees: number; qty: number; total_qty: number;
  commission: number; ecn_fees: number; other_fees: number; liquidity: 'A' | 'R' | '';
};

export type TradeDetail = Trade & {
  notes: string; executions: Execution[]; prevId: number | null; nextId: number | null;
  rulesFollowed: string[]; stopPrice: number | null; metrics: TradeMetrics | null;
};

export type SetupBrief = { trades: number; netPnl: number; winRate: number; profitFactor: number | null; expectancy: number };
export type PlaybookSummary = SetupBrief & { setup: string; hasPlaybook: boolean; rules: number; avgR: number | null; lastTraded: string | null };
export type PlaybookDetail = {
  setup: string; description: string; rules: string[]; examples: Trade[]; updatedAt: number | null;
  stats: Stats & { avgR: number | null }; byHour: ReportRow[]; bySide: ReportRow[];
  followed: { all: SetupBrief; notAll: SetupBrief; unchecked: number } | null;
  recent: Trade[];
};

export type Day = { date: string; net: number; gross: number; fees: number; trades: number; wins: number; losses: number; shares: number; todo: number };

export type Stats = {
  trades: number; openTrades: number; wins: number; losses: number; scratches: number; winRate: number;
  netPnl: number; grossPnl: number; fees: number; avgWin: number; avgLoss: number; largestWin: number; largestLoss: number;
  profitFactor: number | null; expectancy: number; avgHoldWinMs: number; avgHoldLossMs: number; shares: number;
  maxDrawdown: number; drawdownFrom: string | null; drawdownTo: string | null; maxConsecWins: number; maxConsecLosses: number; tradingDays: number; greenDays: number;
  avgDailyPnl: number; bestDay: Day | null; worstDay: Day | null; avgR: number | null; days: Day[];
  volume: number; commission: number; ecnFees: number; otherFees: number;
  avgExitEfficiency: number | null; avgMfe: number | null; avgMae: number | null;
};

export type ReportRow = {
  label: string; trades: number; net: number; avg: number; winRate: number; avgWin: number; avgLoss: number;
  profitFactor: number | null; shares: number; volume: number; fees: number;
  avgMfe: number | null; avgMae: number | null; avgEfficiency: number | null;
};

export type Discipline = { maxDailyLoss: number | null; maxTrades: number | null; revengeMinutes: number };

export type Goals = { weeklyPnl: number | null; monthlyPnl: number | null; winRate: number | null; disciplineScore: number | null };

export type Settings = { timezone: string; setups: string[]; mistakes: string[]; polygonKey: string; discipline: Discipline; goals: Goals };

export type Scenario = { group: string; label: string; detail?: string; pnl: number; trades: number; delta: number };
export type Flag = { tradeId: number; symbol: string; time: number; net: number; reason: string };
export type DisciplineDay = { date: string; net: number; trades: number; revenge: number; afterMaxLoss: number; overLimit: number; score: number };
export type WatchItem = { symbol: string; catalyst: string; levels: string; plan: string };
export type Plan = { date: string; market: string; focus: string; maxLoss: number | null; watchlist: WatchItem[]; updatedAt: number | null };
type PlanBrief = { trades: number; netPnl: number; winRate: number; expectancy: number };

export type Insights = {
  plan: { daysWithPlan: number; onPlan: PlanBrief; offPlan: PlanBrief };
  whatIf: { actual: number; trades: number; scenarios: Scenario[]; top: Scenario[] };
  discipline: {
    settings: Discipline; days: DisciplineDay[]; avgScore: number | null; cleanDays: number;
    streak: { current: number; best: number }; avgCleanDay: number | null; avgOtherDay: number | null;
    revenge: { trades: number; net: number; list: Flag[] };
    afterMaxLoss: { trades: number; net: number; list: Flag[] };
    overLimit: { trades: number; net: number; list: Flag[] };
  };
};
